import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import {
  Users, UserPlus, Mail, Shield, Loader2, Trash2, Crown,
  Clock, X, ChevronDown, Check,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  owner: "bg-amber-500/15 text-amber-400",
  admin: "bg-purple-500/15 text-purple-400",
  developer: "bg-blue-500/15 text-blue-400",
  viewer: "bg-gray-500/15 text-gray-400",
};

const ROLE_ICONS = {
  owner: Crown,
  admin: Shield,
  developer: Users,
  viewer: Users,
};

export default function TeamManagementTab() {
  const { user } = useContext(AuthContext);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("developer");
  const [inviting, setInviting] = useState(false);

  // Role editing
  const [editingRole, setEditingRole] = useState(null);

  const isAdminOrOwner = user?.role === "admin" || user?.role === "owner";

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/team/members`);
      setMembers(res.data.members || []);
      setInvites(res.data.invites || []);
      setRoles(res.data.roles || {});
    } catch {}
    finally { setLoading(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const res = await axios.post(`${API}/team/invite`, { email: inviteEmail, role: inviteRole });
      toast.success(res.data.message);
      setInviteEmail("");
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.post(`${API}/team/update-role`, { user_id: userId, role: newRole });
      toast.success(`Role updated to ${newRole}`);
      setEditingRole(null);
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await axios.post(`${API}/team/remove-member`, { user_id: userId });
      toast.success("Member removed");
      fetchTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/team/invite/${inviteId}`);
      toast.success("Invite cancelled");
      fetchTeam();
    } catch {
      toast.error("Failed to cancel invite");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--qn-primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-management-tab">
      {/* Header Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardContent className="pt-5 text-center">
            <div className="text-3xl font-bold text-[var(--qn-primary)]">{members.length}</div>
            <div className="text-xs text-[var(--qn-foreground-muted)] mt-1">Team Members</div>
          </CardContent>
        </Card>
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardContent className="pt-5 text-center">
            <div className="text-3xl font-bold text-amber-400">{invites.length}</div>
            <div className="text-xs text-[var(--qn-foreground-muted)] mt-1">Pending Invites</div>
          </CardContent>
        </Card>
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardContent className="pt-5 text-center">
            <div className="text-3xl font-bold text-[var(--qn-secondary)]">4</div>
            <div className="text-xs text-[var(--qn-foreground-muted)] mt-1">Roles Available</div>
          </CardContent>
        </Card>
      </div>

      {/* Invite Member */}
      {isAdminOrOwner && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-3">
              <UserPlus className="w-5 h-5 text-[var(--qn-primary)]" />
              Invite Team Member
            </CardTitle>
            <CardDescription className="text-[var(--qn-foreground-muted)]">
              Send an invitation by email. The invite link will be logged to the server console.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="flex gap-3">
              <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="colleague@company.com" type="email" className="flex-1 bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="invite-email-input" required />
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className="bg-[var(--qn-background)] border border-[var(--qn-border)] rounded-md px-3 text-sm" data-testid="invite-role-select">
                <option value="admin">Admin</option>
                <option value="developer">Developer</option>
                <option value="viewer">Viewer</option>
              </select>
              <Button type="submit" className="btn-primary" disabled={inviting} data-testid="send-invite-btn">
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" /> Invite</>}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Members List */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-3">
            <Users className="w-5 h-5 text-[var(--qn-secondary)]" />
            Team Members ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3" data-testid="members-list">
            {members.map((m) => {
              const RoleIcon = ROLE_ICONS[m.role] || Users;
              return (
                <motion.div key={m.user_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-4 bg-[var(--qn-background)] rounded-lg p-4 border border-[var(--qn-border)]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--qn-primary)] to-[var(--qn-secondary)] flex items-center justify-center text-sm font-bold text-white shrink-0">
                    {(m.name || m.email || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{m.name || "Unnamed"}</div>
                    <div className="text-xs text-[var(--qn-foreground-muted)] truncate">{m.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {editingRole === m.user_id ? (
                      <div className="flex gap-1">
                        {["admin", "developer", "viewer"].map(r => (
                          <button key={r} onClick={() => handleUpdateRole(m.user_id, r)} className={`px-2 py-1 text-xs rounded border transition-all ${m.role === r ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)]" : "border-[var(--qn-border)] hover:border-[var(--qn-primary)]"}`} data-testid={`role-option-${r}`}>
                            {r}
                          </button>
                        ))}
                        <button onClick={() => setEditingRole(null)} className="text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)]"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <Badge className={`${ROLE_COLORS[m.role] || ROLE_COLORS.viewer} cursor-${isAdminOrOwner && m.user_id !== user?.user_id && m.role !== "owner" ? "pointer" : "default"}`}
                        onClick={() => isAdminOrOwner && m.user_id !== user?.user_id && m.role !== "owner" && setEditingRole(m.user_id)}
                        data-testid={`member-role-${m.user_id}`}
                      >
                        <RoleIcon className="w-3 h-3 mr-1" />
                        {m.role}
                      </Badge>
                    )}
                    {isAdminOrOwner && m.user_id !== user?.user_id && m.role !== "owner" && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:bg-red-500/10" onClick={() => handleRemoveMember(m.user_id)} data-testid={`remove-member-${m.user_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
            {members.length === 0 && (
              <div className="text-center py-8 text-sm text-[var(--qn-foreground-muted)]">No team members yet. Start by inviting someone above.</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {invites.length > 0 && (
        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-3">
              <Mail className="w-5 h-5 text-amber-400" />
              Pending Invites ({invites.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3" data-testid="invites-list">
              {invites.map((inv) => (
                <div key={inv.invite_id} className="flex items-center gap-4 bg-[var(--qn-background)] rounded-lg p-4 border border-[var(--qn-border)]">
                  <Mail className="w-5 h-5 text-amber-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{inv.email}</div>
                    <div className="text-xs text-[var(--qn-foreground-muted)] flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      Invited {new Date(inv.created_at).toLocaleDateString()}
                      <span className="text-[var(--qn-foreground-subtle)]">Expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <Badge className={ROLE_COLORS[inv.role] || ROLE_COLORS.developer}>{inv.role}</Badge>
                  {isAdminOrOwner && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:bg-red-500/10" onClick={() => handleCancelInvite(inv.invite_id)} data-testid={`cancel-invite-${inv.invite_id}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role Permissions Reference */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardHeader>
          <CardTitle className="font-outfit text-base">Role Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3" data-testid="roles-reference">
            {Object.entries({
              owner: "Full access, billing, team management, can promote to owner",
              admin: "Manage users, API keys, vault, billing, invite members",
              developer: "Create API keys, manage vault, use gateway, view own usage",
              viewer: "View dashboard, providers, models, own usage (read-only)",
            }).map(([role, desc]) => {
              const RIcon = ROLE_ICONS[role] || Users;
              return (
                <div key={role} className="bg-[var(--qn-background)] rounded-lg p-3 border border-[var(--qn-border)]">
                  <div className="flex items-center gap-2 mb-2">
                    <RIcon className="w-4 h-4 text-[var(--qn-primary)]" />
                    <span className="text-sm font-medium capitalize">{role}</span>
                  </div>
                  <p className="text-xs text-[var(--qn-foreground-muted)] leading-relaxed">{desc}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
